import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { catalogApi, type CategoryNode, type ProductSummary } from "../../api/catalog";
import type { ApiError } from "../../api/client";
import { SalesRepPopup } from "../../components/SalesRepPopup";
import { ProductCard } from "./ProductCard";
import { useCatalogOrdering } from "./useCatalogOrdering";

export default function CatalogCategoryPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [tree, setTree] = useState<CategoryNode[] | null>(null);
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { canOrder, orderedIds, addToOrder, openDatasheet } = useCatalogOrdering();

  const locale = i18n.resolvedLanguage ?? "en";
  const node = useMemo(() => (tree ? findNode(tree, slug ?? "") : null), [tree, slug]);
  const path = useMemo(() => (tree && slug ? findPath(tree, slug) : []), [tree, slug]);
  const imageByCategory = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: CategoryNode[]) => {
      for (const n of nodes) { if (n.imageUrl) map.set(n.id, n.imageUrl); walk(n.children); }
    };
    if (tree) walk(tree);
    return map;
  }, [tree]);

  useEffect(() => {
    void catalogApi.tree(locale).then(setTree).catch((e: ApiError) => setError(e.message));
  }, [locale]);

  useEffect(() => {
    if (!node) return;
    setProducts(null);
    void catalogApi
      .listProducts({ categoryId: node.id, includeDescendants: true, locale, status: "PUBLISHED", page: 0, size: 200 })
      .then((p) => setProducts(p.content.filter(Boolean)))
      .catch((e: ApiError) => setError(e.message));
  }, [node, locale]);

  if (!tree) return <p style={{ color: "var(--ink-3)", padding: 20 }}>{t("catalog.loading", "Loading…")}</p>;
  if (!node) return <p style={{ color: "var(--ink-3)", padding: 20 }}>{t("catalog.categoryNotFound", "Category not found.")}</p>;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("catalog.breadcrumb.aria", "Breadcrumb")}>
            <span><Link to="/catalog" style={crumbLink}>{t("catalog.breadcrumb", "Catalog")}</Link></span>
            {path.slice(0, -1).map((c) => (
              <span key={c.id}><Link to={`/catalog/${c.slug}`} style={crumbLink}>{c.name}</Link></span>
            ))}
            <span style={crumbCurrent}>{node.name}</span>
          </nav>
          <h1>{node.name}</h1>
          <div className="sub">
            {products
              ? t("catalog.productCount", "{{count}} product", { count: products.length })
              : t("catalog.loadingProducts", "Loading products…")}
            {node.children.length > 0
              ? ` · ${t("catalog.subcategoryCount", "{{count}} subcategories", { count: node.children.length })}`
              : ""}
          </div>
        </div>
        <div className="page-actions">
          <Link to="/catalog" className="btn">{t("catalog.backToCatalog", "← Back to catalog")}</Link>
        </div>
      </div>

      {error && <div style={errorBox}>{error}</div>}

      {node.children.length > 0 && (
        <section style={card}>
          <h2 style={sectionTitle}>{t("catalog.subcategoriesHeading", "Subcategories")}</h2>
          <div style={subGrid}>
            {node.children.map((c) => (
              <Link key={c.id} to={`/catalog/${c.slug}`} className="row-link" style={subCard}>
                <strong style={{ fontSize: 14, color: "var(--ink)" }}>{c.name}</strong>
                <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {c.children.length > 0
                    ? t("catalog.subcategoryCount", "{{count}} subcategories", { count: c.children.length })
                    : t("catalog.viewProducts", "View products →")}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!products ? (
        <p style={{ color: "var(--ink-3)", padding: 20 }}>{t("catalog.loadingProducts", "Loading products…")}</p>
      ) : products.length === 0 ? (
        <div className="empty">{t("catalog.emptyCategory", "No products in this category yet.")}</div>
      ) : (
        <div className="cat-grid">
          {products.map((p) => (
            <ProductCard
              key={p.id}
              p={p}
              imageUrl={(p.categoryId ? imageByCategory.get(p.categoryId) : undefined) ?? node.imageUrl ?? null}
              onAddToOrder={() => addToOrder(p)}
              onDatasheet={() => openDatasheet(p)}
              canOrder={canOrder}
              ordered={orderedIds.has(p.id)}
            />
          ))}
        </div>
      )}

      <SalesRepPopup />
    </div>
  );
}

function findNode(nodes: CategoryNode[], slug: string): CategoryNode | null {
  for (const n of nodes) {
    if (n.slug === slug) return n;
    const c = findNode(n.children, slug);
    if (c) return c;
  }
  return null;
}

/** Root→node chain, so the breadcrumb shows (and links) every level. */
function findPath(nodes: CategoryNode[], slug: string, trail: CategoryNode[] = []): CategoryNode[] {
  for (const n of nodes) {
    const next = [...trail, n];
    if (n.slug === slug) return next;
    const deep = findPath(n.children, slug, next);
    if (deep.length) return deep;
  }
  return [];
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", boxShadow: "var(--shadow-1)", padding: 18,
};
const sectionTitle: CSSProperties = {
  margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5,
};
const subGrid: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 };
const subCard: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 3, padding: "12px 14px",
  background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", textDecoration: "none",
};
const crumbLink: CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
const errorBox: CSSProperties = {
  padding: 10, marginBottom: 4, background: "var(--err-bg)", color: "var(--err)", borderRadius: "var(--r-sm)", fontSize: 13,
};
